// src/pages/admin/linkedin-generator.tsx
//
// Générateur de posts LinkedIn IA pour le compte perso de Gérald.
// Workflow :
//   1. Admin choisit un angle (parmi 20 templates) ou rédige un brief libre
//   2. (Optionnel) précise un sujet / chiffre / contexte
//   3. Submit → call Edge Function openai-chat (audience = linkedin_writer)
//   4. Affiche 3 versions distinctes, copy-to-clipboard sur chacune
//
// Réutilise l'infra openai-chat existante (RESEND_API_KEY déjà en place
// côté Supabase secrets).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Loader2,
  Copy,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  Wand2,
  Linkedin,
} from 'lucide-react';
import { useAuth } from '../../AuthProvider';
import { supabase } from '../../lib/supabase';

// — 20 angles de posts (templates de brief) —
const ANGLES = [
  {
    label: '📊 Chiffre étonnant',
    brief:
      'Un chiffre marquant du marché RH ou recrutement. Ex: "64% des candidats déçus boycottent ensuite la marque B2C". Source à inventer si besoin (APEC, INSEE, Talent Board).',
  },
  {
    label: '🔥 Opinion contrarian',
    brief:
      "Prendre le contre-pied d'une croyance répandue dans le recrutement. Ex: \"Le télétravail complet en B2B est en train de devenir un piège pour les jeunes cadres\".",
  },
  {
    label: '👀 Behind-the-scenes',
    brief:
      'Ce qu\'on observe en pratique chez nos clients PME PACA, et que personne ne dit publiquement. Ex: "Ce qu\'on voit dans 90% des recrutements ratés en PACA".',
  },
  {
    label: '📈 Case study anonymisé',
    brief:
      'Story arc : un client (anonymisé "une PME industrielle de 200 personnes en PACA") avait tel problème → on a fait ça → résultats chiffrés. Sans citer les marques.',
  },
  {
    label: '🌍 Lecture marché PACA',
    brief:
      'Insight très local : salaires cadres Marseille 2026, secteurs en tension Aix, dynamique Toulon défense, etc.',
  },
  {
    label: '💡 Conseil tactique court',
    brief:
      'Un conseil pratique, actionnable, court — pour un DRH PME. Ex: "Comment éviter le no-show entre signature et J+1".',
  },
  {
    label: '⚠️ Anti-pattern / erreur',
    brief:
      "Une erreur qu'on voit constamment et qu'il faut arrêter de faire. Ex: \"Pourquoi votre fiche de poste tue votre recrutement avant même l'entretien\".",
  },
  {
    label: '⚔️ Comparaison face-à-face',
    brief:
      'Mettre en opposition deux approches. Ex: "Cabinet classique vs cabinet augmenté — 5 différences invisibles au signing".',
  },
  {
    label: '😅 Confession / aveu',
    brief:
      'Aveu personnel humble qui désamorce. Ex: "J\'ai longtemps cru que l\'IA allait remplacer les recruteurs. Voilà ce que j\'ai appris en 4 ans."',
  },
  {
    label: '📚 Mythe RH à déconstruire',
    brief:
      'Casser un mythe répandu. Ex: "Non, augmenter le salaire ne règle PAS le problème de rétention. Voilà ce qui marche vraiment."',
  },
  {
    label: '🔮 Prédiction marché',
    brief:
      "Prédiction sur l'évolution du marché RH/recrutement en 2026-2027. Doit être documentée, pas spéculative.",
  },
  {
    label: '🤖 Cas d\'usage IA pratique',
    brief:
      "Comment l'IA est réellement utilisée en recrutement (sourcing sémantique, scoring CV, génératif marque employeur), avec les vrais résultats.",
  },
  {
    label: '⚖️ IA et jugement humain',
    brief:
      "L'IA n'est pas la solution magique. Ce qu'elle fait bien, ce qu'elle ne fera jamais. Angle: jugement humain + outils, pas IA seule.",
  },
  {
    label: '💬 Témoignage candidat anonymisé',
    brief:
      "Citation/témoignage d'un candidat (anonymisé) qui révèle un insight fort sur l'expérience candidat.",
  },
  {
    label: '💸 Calcul de coût caché',
    brief:
      "Faire le calcul du vrai coût d'un recrutement raté, d'un no-show, d'un turnover. Mettre des chiffres concrets PACA.",
  },
  {
    label: '🎁 Annonce de ressource gratuite',
    brief:
      'Annoncer un livre blanc, calculateur, dossier qu\'on vient de publier. Avec angle "voilà ce que vous y trouverez".',
  },
  {
    label: '⏱️ Lesson learned chiffrée',
    brief:
      "Sur 100 recrutements faits cette année, voilà ce qu'on a appris. 3-5 leçons chiffrées et concrètes.",
  },
  {
    label: '🎯 Mini-framework / méthode',
    brief:
      'Donner un mini-framework actionnable. Ex: "La règle des 3X pour calibrer un package commercial en PACA".',
  },
  {
    label: '📰 Réaction à actualité RH',
    brief:
      "Réaction à une actualité récente du marché RH (annonce gouvernementale, étude majeure, événement secteur). Angle personnel.",
  },
  {
    label: '✏️ Brief libre',
    brief: '',
  },
];

interface GeneratedPost {
  title: string;
  post: string;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export default function LinkedInGeneratorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [selectedAngle, setSelectedAngle] = useState<number>(0);
  const [brief, setBrief] = useState('');
  const [topic, setTopic] = useState('');
  const [cta, setCta] = useState(
    'Lien dans mon profil pour télécharger le livre blanc.',
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posts, setPosts] = useState<GeneratedPost[]>([]);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [savedIdx, setSavedIdx] = useState<number | null>(null);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);

  // Auth check
  useEffect(() => {
    async function check() {
      if (!user) {
        navigate('/');
        return;
      }
      const { data } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();
      if (data?.role !== 'admin' && data?.role !== 'editor') {
        navigate('/');
        return;
      }
      setIsAdmin(true);
      setAuthChecked(true);
    }
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Met à jour le brief quand on change d'angle
  useEffect(() => {
    setBrief(ANGLES[selectedAngle]?.brief ?? '');
  }, [selectedAngle]);

  async function handleGenerate() {
    setError(null);
    setPosts([]);
    setLoading(true);

    try {
      // Construit le brief utilisateur final
      const userMessage = [
        `# Angle souhaité`,
        ANGLES[selectedAngle]?.label ?? 'Brief libre',
        '',
        `# Brief / contexte`,
        brief.trim() || '(libre)',
        topic.trim() ? `\n# Sujet précis\n${topic.trim()}` : '',
        cta.trim() ? `\n# CTA à intégrer en fin de post\n${cta.trim()}` : '',
      ]
        .filter(Boolean)
        .join('\n');

      const res = await fetch(
        `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/openai-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${SUPABASE_ANON}`,
            apikey: SUPABASE_ANON,
          },
          body: JSON.stringify({
            audience: 'linkedin_writer',
            messages: [{ role: 'user', content: userMessage }],
            stream: false, // on veut le JSON complet
            model: 'gpt-4o-mini',
            temperature: 0.7,
          }),
        },
      );

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Erreur ${res.status} — ${errText.slice(0, 200)}`);
      }

      // Reader pour stream (même en stream=false l'edge function peut renvoyer SSE)
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let acc = '';

      if (reader) {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          // Cas SSE — parser les data:
          const sseLines = chunk
            .split('\n')
            .filter((l) => l.startsWith('data: '));
          if (sseLines.length > 0) {
            for (const line of sseLines) {
              const data = line.replace(/^data:\s*/, '').trim();
              if (data === '[DONE]') continue;
              try {
                const parsed = JSON.parse(data);
                acc +=
                  parsed?.choices?.[0]?.delta?.content ??
                  parsed?.choices?.[0]?.message?.content ??
                  '';
              } catch {
                acc += data;
              }
            }
          } else {
            // Plain JSON
            acc += chunk;
          }
        }
      } else {
        acc = await res.text();
      }

      // Parser le JSON renvoyé par l'IA
      // L'IA peut parfois entourer de ```json … ```, on nettoie
      const cleaned = acc
        .trim()
        .replace(/^```json\s*/i, '')
        .replace(/^```\s*/i, '')
        .replace(/```\s*$/i, '')
        .trim();

      let parsed: { versions: GeneratedPost[] };
      try {
        parsed = JSON.parse(cleaned);
      } catch {
        throw new Error(
          "L'IA n'a pas renvoyé du JSON valide. Réessaye ou ajuste le brief.",
        );
      }

      if (!parsed?.versions || !Array.isArray(parsed.versions)) {
        throw new Error("Format de réponse inattendu.");
      }

      setPosts(parsed.versions);
    } catch (e) {
      console.error('[linkedin-gen] error:', e);
      setError(e instanceof Error ? e.message : 'Erreur inconnue.');
    } finally {
      setLoading(false);
    }
  }

  async function copyToClipboard(text: string, idx: number) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
  }

  /**
   * Sauvegarde un post sur /insights (table articles, category = 'insights').
   * Génère un slug auto à partir du titre. Si le slug existe déjà, on suffixe
   * avec un timestamp court pour éviter le conflit.
   */
  async function saveToInsights(post: GeneratedPost, idx: number) {
    setSavingIdx(idx);
    try {
      // Slug auto : kebab-case du titre, max 60 chars
      const baseSlug = post.title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .slice(0, 60);

      // Vérifie l'unicité — ajout d'un suffixe si nécessaire
      const { data: existing } = await supabase
        .from('articles')
        .select('id')
        .eq('slug', baseSlug)
        .maybeSingle();

      const finalSlug = existing
        ? `${baseSlug}-${Date.now().toString(36)}`
        : baseSlug;

      // Excerpt = 160 premiers chars du post (sans le titre)
      const excerpt = post.post.slice(0, 160).replace(/[,. ]+$/, '') + '…';

      const { error: insertErr } = await supabase.from('articles').insert([
        {
          title: post.title,
          slug: finalSlug,
          content: post.post,
          excerpt,
          author: 'Gérald Gaillard',
          category: 'insights',
          published: true,
        },
      ]);

      if (insertErr) {
        console.error('[save insight] error:', insertErr.message);
        alert(`Erreur sauvegarde : ${insertErr.message}`);
        return;
      }

      setSavedIdx(idx);
      setTimeout(() => setSavedIdx(null), 3000);
    } catch (e) {
      console.error('[save insight] catch:', e);
      alert('Erreur réseau, réessaye.');
    } finally {
      setSavingIdx(null);
    }
  }

  if (!authChecked || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  const inputClass =
    'w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all';
  const labelClass =
    'block text-xs font-bold text-[#30475e] uppercase tracking-wider mb-1.5';

  return (
    <div className="min-h-screen bg-neutral-50 pb-20">
      {/* Header admin */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/users')}
              className="text-neutral-500 hover:text-[#30475e] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <Linkedin className="w-5 h-5 text-[#0077b5]" />
            <h1 className="text-lg font-bold text-[#30475e]">
              Générateur de posts LinkedIn
            </h1>
          </div>
          <span className="text-xs text-neutral-400">
            Audience : DRH/dirigeants PME PACA
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-8">
        {/* Colonne form */}
        <div className="space-y-5">
          <div className="rounded-2xl bg-white ring-1 ring-neutral-200 p-6">
            <h2 className="text-base font-bold text-[#30475e] mb-4 flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-[#d03484]" />
              Brief du post
            </h2>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>Angle du post</label>
                <select
                  value={selectedAngle}
                  onChange={(e) => setSelectedAngle(parseInt(e.target.value, 10))}
                  className={inputClass}
                >
                  {ANGLES.map((a, i) => (
                    <option key={i} value={i}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  Brief / contexte
                  <span className="text-neutral-400 font-normal normal-case ml-1">
                    (le template auto-rempli est éditable)
                  </span>
                </label>
                <textarea
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={5}
                  className={inputClass}
                  placeholder="Ex: Les recrutements ratés coûtent en moyenne 3x le salaire annuel sur 18 mois..."
                />
              </div>

              <div>
                <label className={labelClass}>
                  Sujet précis (optionnel)
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className={inputClass}
                  placeholder="Ex: Recrutement commercial à Marseille en 2026"
                />
              </div>

              <div>
                <label className={labelClass}>CTA en fin de post</label>
                <input
                  type="text"
                  value={cta}
                  onChange={(e) => setCta(e.target.value)}
                  className={inputClass}
                  placeholder="Ex: Lien dans mon profil pour télécharger…"
                />
              </div>

              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-3 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0 whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Génération…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Générer 3 versions
                  </>
                )}
              </button>

              {error && (
                <div className="rounded-xl bg-red-50 ring-1 ring-red-200 px-3 py-2 text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-[#fbfaf8] ring-1 ring-neutral-200 p-5 text-xs text-neutral-600">
            <p className="font-semibold text-[#30475e] mb-2">💡 Conseils prod</p>
            <ul className="space-y-1.5 list-disc list-inside">
              <li>Cadence cible : 4-5 posts/semaine.</li>
              <li>Varie les angles entre 2 posts consécutifs.</li>
              <li>Édite la version qui te plaît 30 sec avant de publier.</li>
              <li>Publie 8h-9h ou 12h-13h pour max engagement.</li>
              <li>Réponds aux 10 premiers commentaires dans les 30 min.</li>
            </ul>
          </div>
        </div>

        {/* Colonne résultats */}
        <div className="space-y-4">
          {posts.length === 0 && !loading && (
            <div className="rounded-2xl border-2 border-dashed border-neutral-200 bg-white p-12 text-center">
              <Sparkles className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
              <p className="text-sm text-neutral-500">
                Choisis un angle, ajuste le brief, clique sur "Générer 3 versions".
                <br />
                Tu reçois 3 propositions distinctes en 5-10 sec.
              </p>
            </div>
          )}

          {loading && (
            <div className="rounded-2xl bg-white ring-1 ring-neutral-200 p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#d03484] mx-auto mb-3" />
              <p className="text-sm text-neutral-500">
                Génération en cours… (5-10 sec)
              </p>
            </div>
          )}

          {posts.map((p, i) => (
            <div
              key={i}
              className="rounded-2xl bg-white ring-1 ring-neutral-200 shadow-sm overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 py-3 bg-[#fbfaf8] border-b border-neutral-100">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] text-white text-xs font-bold">
                    {i + 1}
                  </span>
                  <span className="text-sm font-semibold text-[#30475e]">
                    {p.title}
                  </span>
                  <span className="text-[10px] text-neutral-400 ml-2">
                    {p.post.length} caractères
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => saveToInsights(p, i)}
                    disabled={savingIdx === i || savedIdx === i}
                    title="Republier le post sur /insights (page SEO publique)"
                    className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-neutral-200 hover:ring-[#d03484]/40 bg-white text-[#30475e] text-xs font-semibold px-3 py-1.5 transition-all disabled:opacity-60"
                  >
                    {savingIdx === i ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Sauv…
                      </>
                    ) : savedIdx === i ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        Sur /insights
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5" />
                        Sauver /insights
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => copyToClipboard(p.post, i)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#30475e] hover:bg-[#1f3245] text-white text-xs font-semibold px-3 py-1.5 transition-colors"
                  >
                    {copiedIdx === i ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Copié !
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copier
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="px-5 py-4">
                <pre className="whitespace-pre-wrap font-sans text-sm text-[#30475e] leading-relaxed">
                  {p.post}
                </pre>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
